import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const EVENT_TYPES = [
  "Live performance",
  "Club appearance",
  "Festival",
  "Private event",
  "Interview / media",
  "Feature / collaboration",
  "Other",
];

const EMPTY = { name: "", email: "", eventType: "", eventDate: "", cityVenue: "", budget: "", message: "" };

export default function BookingSection({ content }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      toast.success("Booking enquiry sent. The Zee Nation team will be in touch.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send enquiry. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="relative z-10 bg-[#09090b] py-20 sm:py-28 lg:py-36 border-t border-zinc-900" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-5 gap-12 lg:gap-16">
        <div className="lg:col-span-2">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-rose-500">Bookings &amp; Enquiries</span>
          <h2 className="mt-5 font-anton uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] text-white" data-testid="booking-headline">
            Let's make<br />it a moment.
          </h2>
          <p className="mt-8 text-zinc-400 text-base leading-relaxed">{content.booking_subtext}</p>
          <div className="mt-8 p-5 bg-[#121216] border border-zinc-800 rounded-md">
            <p className="font-mono text-xs uppercase tracking-wider text-rose-500 mb-2">Booking tip</p>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Include your event date, city, venue and the type of appearance so the team can respond with the right information.
            </p>
          </div>
        </div>

        <div className="lg:col-span-3">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full min-h-[360px] flex flex-col items-start justify-center bg-[#121216] border border-rose-600/40 rounded-md p-10 shadow-[0_0_40px_rgba(225,29,72,0.15)]"
              data-testid="booking-confirmation"
            >
              <CheckCircle2 className="w-14 h-14 text-rose-500 mb-6" />
              <h3 className="font-anton uppercase text-3xl sm:text-4xl text-white leading-none">Enquiry received</h3>
              <p className="mt-4 text-zinc-400 max-w-md leading-relaxed">
                Thank you for reaching out to Mr Zee / Zee Nation. Your booking enquiry has been logged and the team will respond to you directly.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 inline-flex items-center gap-2 border border-zinc-600 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-3 rounded-sm transition-colors"
                data-testid="booking-new-btn"
              >
                Send another enquiry
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="booking-form">
              <Field label="Your name" required>
                <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="booking-name" placeholder="Jane Promoter" />
              </Field>
              <Field label="Email address" required>
                <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="booking-email" placeholder="you@email.com" />
              </Field>
              <Field label="Event type" required>
                <select required value={form.eventType} onChange={update("eventType")} className={inputCls} data-testid="booking-event-type">
                  <option value="" disabled>Select event type</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#121216]">{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Event date" required>
                <input required type="date" value={form.eventDate} onChange={update("eventDate")} className={inputCls} data-testid="booking-date" />
              </Field>
              <Field label="City / venue" required>
                <input required value={form.cityVenue} onChange={update("cityVenue")} className={inputCls} data-testid="booking-venue" placeholder="City, venue name" />
              </Field>
              <Field label="Budget (optional)">
                <input value={form.budget} onChange={update("budget")} className={inputCls} data-testid="booking-budget" placeholder="e.g. $2,000" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Tell us about the event" required>
                  <textarea required rows={4} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="booking-message" placeholder="Share the details, line-up, timing and anything else we should know." />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-mono text-xs uppercase tracking-[0.15em] px-10 py-4 rounded-sm transition-colors shadow-[0_0_30px_rgba(225,29,72,0.3)]"
                  data-testid="booking-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send booking enquiry <ArrowUpRight className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-[#0c0c0f] border border-zinc-800 focus:border-rose-600 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-2 block">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
    </label>
  );
}
